import type { Guest } from "@/types/Guest";

/**
 * Merge a `guestDataUpdated` popup-save patch into a locally held guest row.
 *
 * API bookkeeping keys never land on the row: `guestId` (identity),
 * `acceptanceState` / `forceWaitlist` / `overrideRules` (resolution is owned
 * exclusively by the `guestAcceptanceResolved` event — a merged state flip
 * would leave a resolved row rendered actionable), and `clearFields` (applied
 * below as unsets, not copied).
 *
 * `additionalFields` merges ONE LEVEL DEEP: the patch carries the popup's
 * full current AF map MINUS persisted-IMAGE echoes (see
 * `stripPersistedImageEchoes` in parser.ts), so replacing the row's map
 * wholesale would blank IMAGE AF cells.
 *
 * `clearFields` comes in two shapes (see `parseGuestsSpecial` in parser.ts):
 * top-level names ("gender", "emailOptIn") → unset on the row;
 * "additionalFields.<name>" paths → drop <name> from the merged AF map.
 */
export function mergeGuestPatch(row: Guest, patch: Partial<Guest>): Guest {
  const {
    guestId: _guestId,
    acceptanceState: _acceptanceState,
    forceWaitlist: _forceWaitlist,
    overrideRules: _overrideRules,
    clearFields,
    additionalFields,
    ...rest
  } = patch;

  const mergedAf =
    additionalFields || row.additionalFields
      ? { ...(row.additionalFields ?? {}), ...(additionalFields ?? {}) }
      : undefined;

  const merged: Guest = {
    ...row,
    ...rest,
    ...(mergedAf !== undefined ? { additionalFields: mergedAf } : {}),
  };

  const AF_PREFIX = "additionalFields.";
  clearFields?.forEach((path) => {
    if (path.startsWith(AF_PREFIX)) {
      if (merged.additionalFields)
        delete merged.additionalFields[path.slice(AF_PREFIX.length)];
    } else {
      (merged as Record<string, unknown>)[path] = undefined;
    }
  });

  return merged;
}

/**
 * Replace a locally held row with the server's version, after a reconcile.
 *
 * Deliberately NOT {@link mergeGuestPatch}. That function strips
 * `acceptanceState` because a popup patch must never flip a row's resolution
 * state behind the `guestAcceptanceResolved` event. Here the opposite is true:
 * the server's `state` / `acceptanceState` is the entire reason we re-read the
 * guest — an automation may have changed exactly those, and dropping them would
 * make the reconcile a no-op for the case it exists to fix.
 *
 * The only thing carried over from the old row is data the reconcile projection
 * does not return. `GUEST_REFETCH_FIELDS` omits `companions`, and a lookup-only
 * field like `mainGuestDTO` can be absent depending on the surface — blanking
 * either would wipe the companion link out of the grid.
 */
export function applyServerTruth(row: Guest, fresh: Guest): Guest {
  return {
    ...fresh,
    mainGuestDTO: fresh.mainGuestDTO ?? row.mainGuestDTO,
    companions: fresh.companions ?? row.companions,
    mainGuest: fresh.mainGuest ?? row.mainGuest,
  };
}
